import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useToast } from "@/components/ui/use-toast"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { addPermissionToGroup, addUserToGroup, createGroup, deleteGroup, getGroups, removePermissionFromGroup, removeUserFromGroup, getGroupUsers, getGroupPermissions } from '@/api/groups';
import { getUsers } from '@/api/users';
import { getPermissions } from '@/api/permissions';
import { getServices } from '@/api/services';

interface Group {
  id: string;
  name: string;
  permissions: Permission[];
  users: User[];
}

interface Permission {
  id: string;
  name: string;
  serviceId: string;
}

interface User {
  id: string;
  email: string;
}

interface Service {
  id: string;
  name: string;
}

const Groups = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '' });
  const { toast } = useToast();

  useEffect(() => {
    fetchGroups();
    fetchPermissions();
    fetchUsers();
    fetchServices();
  }, []);

  const fetchGroups = async () => {
    try {
      const groupData = await getGroups();
      const groupsWithDetails = await Promise.all(
        groupData.map(async (group: Group) => {
          const users = await getGroupUsers(group.id);
          const permissions = await getGroupPermissions(group.id);
          return {
            ...group,
            users,
            permissions
          };
        })
      );
      setGroups(groupsWithDetails);
    } catch (error) {
      console.error(`Error in fetchGroups: ${error}`); 
      toast({
        title: "Error",
        description: "Failed to fetch groups",
        variant: "destructive",
      });
    }
  };

  const fetchPermissions = async () => {
    try {
      const data = await getPermissions();
      setPermissions(data);
    } catch (error) {
      console.error(`Error in fetchPermissions: ${error}`);
      toast({
        title: "Error",
        description: "Failed to fetch permissions",
        variant: "destructive",
      });
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error(`Error in fetchUsers: ${error}`);
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    }
  };

  const fetchServices = async () => {
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      console.error(`Error in fetchServices: ${error}`);
      toast({
        title: "Error",
        description: "Failed to fetch services",
        variant: "destructive",
      });
    }
  };

  const handleCreate = async () => {
    try {
      await createGroup(newGroup);
      setNewGroup({ name: '' });
      setIsOpen(false);
      fetchGroups();
      toast({
        title: "Success",
        description: "Group created successfully",
      });
    } catch (error) {
      console.error(`Error in handleCreate: ${error}`);
      toast({
        title: "Error",
        description: "Failed to create group",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGroup(id);
      fetchGroups();
      toast({
        title: "Success",
        description: "Group deleted successfully",
      });
    } catch (error) {
      console.error(`Error in handleDelete: ${error}`);
      toast({
        title: "Error",
        description: "Failed to delete group",
        variant: "destructive",
      });
    }
  };

  const handleAddPermission = async (groupId: string, permissionId: string) => {
    try {
      await addPermissionToGroup(groupId, permissionId);
      fetchGroups();
      toast({
        title: "Success",
        description: "Permission added successfully",
      });
    } catch (error) {
      console.error(`Error in handleAddPermission: ${error}`);
      toast({
        title: "Error",
        description: "Failed to add permission",
        variant: "destructive",
      });
    }
  };

  const handleRemovePermission = async (groupId: string, permissionId: string) => {
    try {
      await removePermissionFromGroup(groupId, permissionId);
      fetchGroups();
      toast({
        title: "Success",
        description: "Permission removed successfully",
      });
    } catch (error) {
      console.error(`Error in handleRemovePermission: ${error}`);
      toast({
        title: "Error",
        description: "Failed to remove permission",
        variant: "destructive",
      });
    }
  };

  const handleAddUser = async (groupId: string, userId: string) => {
    try {
      await addUserToGroup(groupId, userId);
      fetchGroups();
      toast({
        title: "Success",
        description: "User added successfully",
      });
    } catch (error) {
      console.error(`Error in handleAddUser: ${error}`);
      toast({
        title: "Error",
        description: "Failed to add user",
        variant: "destructive",
      });
    }
  };

  const handleRemoveUser = async (groupId: string, userId: string) => {
    try {
      await removeUserFromGroup(groupId, userId);
      fetchGroups();
      toast({
        title: "Success",
        description: "User removed successfully",
      });
    } catch (error) {
      console.error(`Error in handleRemoveUser: ${error}`);
      toast({
        title: "Error",
        description: "Failed to remove user",
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Groups</h1>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button>Create Group</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Group</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="Group Name"
                value={newGroup.name}
                onChange={(e) => setNewGroup({ name: e.target.value })}
              />
              <Button onClick={handleCreate}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Permissions</TableHead>
            <TableHead>Users</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups?.map((group) => (
            <TableRow key={group.id}>
              <TableCell>{group.name}</TableCell>
              <TableCell>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {group.permissions?.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        No permissions assigned
                      </div>
                    ) : (
                      group.permissions?.map((permission) => (
                        <div 
                          key={permission.id} 
                          className="flex items-center gap-2 bg-secondary/50 px-3 py-1 rounded-md"
                        >
                          <span className="font-medium">
                            {permission.name}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            ({services.find((s) => s.id === permission.serviceId)?.name})
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => handleRemovePermission(group.id, permission.id)}
                          >
                            ×
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                  <Select onValueChange={(value) => handleAddPermission(group.id, value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Add permission" />
                    </SelectTrigger>
                    <SelectContent>
                      {permissions
                        ?.filter((p) => !group.permissions?.some((gp) => gp.id === p.id))
                        .map((permission) => (
                          <SelectItem key={permission.id} value={permission.id}>
                            {permission.name} ({services?.find((s) => s.id === permission.serviceId)?.name})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {group.users?.length === 0 ? (
                      <div className="text-sm text-muted-foreground">
                        No users assigned
                      </div>
                    ) : (
                      group.users?.map((user) => (
                        <div 
                          key={user.id} 
                          className="flex items-center gap-2 bg-secondary/50 px-3 py-1 rounded-md"
                        >
                          <span className="font-medium">
                            {user.email}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => handleRemoveUser(group.id, user.id)}
                          >
                            ×
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                  <Select onValueChange={(value) => handleAddUser(group.id, value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Add user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users
                        ?.filter((u) => !group.users?.some((gu) => gu.id === u.id))
                        .map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.email}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </TableCell>
              <TableCell>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(group.id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default Groups; 
